import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { TestHarness, SimulatedParticipant, ParticipantStats } from './SimulatedParticipant';
import { NETWORK_PROFILES, ProfileName } from './BandwidthSimulator';
import { TestPattern, FakeVideoGenerator } from './FakeVideoGenerator';

const PATTERNS: { value: TestPattern; label: string }[] = [
  { value: 'color-bars', label: 'Color Bars' },
  { value: 'grid', label: 'Grid (Resolution Test)' },
  { value: 'gradient', label: 'Animated Gradient' },
  { value: 'moving-box', label: 'Moving Box (FPS Test)' },
  { value: 'timestamp', label: 'Timestamp' },
  { value: 'noise', label: 'Noise' },
];

export function TestPage() {
  const navigate = useNavigate();
  const [harness, setHarness] = useState<TestHarness | null>(null);
  const [roomCode, setRoomCode] = useState<string | null>(null);
  const [participants, setParticipants] = useState<SimulatedParticipant[]>([]);
  const [stats, setStats] = useState<Record<string, ParticipantStats>>({});
  const [isStreaming, setIsStreaming] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const logRef = useRef<HTMLDivElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const previewGeneratorRef = useRef<FakeVideoGenerator | null>(null);

  // Form states
  const [modelNetwork, setModelNetwork] = useState<ProfileName>('fiber');
  const [artistNetwork, setArtistNetwork] = useState<ProfileName>('cable');
  const [artistCount, setArtistCount] = useState(5);
  const [modelPattern, setModelPattern] = useState<TestPattern>('grid');
  const [modelResolution, setModelResolution] = useState<'1080p' | '4k'>('4k');
  const [receivedFrameUrl, setReceivedFrameUrl] = useState<string | null>(null);

  // Update stats periodically
  useEffect(() => {
    if (!harness) return;

    const interval = setInterval(() => {
      setStats(harness.getAllStats());
      setParticipants([...harness.getParticipants()]);
    }, 1000);

    return () => clearInterval(interval);
  }, [harness]);

  // Auto-scroll log
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [log]);

  // Preview generator
  useEffect(() => {
    if (!previewCanvasRef.current) return;

    const resolutions = {
      '1080p': { width: 1920, height: 1080 },
      '4k': { width: 3840, height: 2160 },
    };
    const { width, height } = resolutions[modelResolution];

    previewGeneratorRef.current = new FakeVideoGenerator({
      width: width / 4, // Scale down for preview
      height: height / 4,
      frameRate: 15,
      pattern: modelPattern,
      label: `Preview (${modelResolution})`,
    });

    const stream = previewGeneratorRef.current.start();
    const video = document.createElement('video');
    video.srcObject = stream;
    video.autoplay = true;
    video.muted = true;

    const canvas = previewCanvasRef.current;
    const ctx = canvas.getContext('2d');

    const drawFrame = () => {
      if (ctx && video.readyState >= 2) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      }
      requestAnimationFrame(drawFrame);
    };
    drawFrame();

    return () => {
      previewGeneratorRef.current?.stop();
      video.srcObject = null;
    };
  }, [modelPattern, modelResolution]);

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLog(prev => [...prev.slice(-100), `[${timestamp}] ${message}`]);
  };

  const handleCreateSession = async () => {
    try {
      const newHarness = new TestHarness();
      const code = await newHarness.createRoom();
      setHarness(newHarness);
      setRoomCode(code);
      addLog(`Created room: ${code}`);
    } catch (err) {
      addLog(`Error creating room: ${err}`);
    }
  };

  const handleAddModel = async () => {
    if (!harness) return;

    try {
      const model = await harness.addParticipant({
        name: 'Model',
        role: 'model',
        resolution: modelResolution,
        videoPattern: modelPattern,
        networkProfile: modelNetwork,
      });
      addLog(`Added model with ${NETWORK_PROFILES[modelNetwork].name} network`);
      setParticipants([...harness.getParticipants()]);
    } catch (err) {
      addLog(`Error adding model: ${err}`);
    }
  };

  const handleAddArtists = async () => {
    if (!harness) return;

    try {
      addLog(`Adding ${artistCount} artists...`);

      for (let i = 0; i < artistCount; i++) {
        const artist = await harness.addParticipant({
          name: `Artist ${i + 1}`,
          role: 'artist',
          resolution: '360p',
          networkProfile: artistNetwork,
        });

        // Set up frame receiving callback for first artist to show preview
        if (i === 0) {
          artist.onFrame((metadata, blob) => {
            const url = URL.createObjectURL(blob);
            setReceivedFrameUrl(prev => {
              if (prev) URL.revokeObjectURL(prev);
              return url;
            });
            addLog(`Artist 1 received frame: ${metadata.width}x${metadata.height}`);
          });
        }
      }

      addLog(`Added ${artistCount} artists with ${NETWORK_PROFILES[artistNetwork].name} network`);
      setParticipants([...harness.getParticipants()]);
    } catch (err) {
      addLog(`Error adding artists: ${err}`);
    }
  };

  const handleStartStreaming = () => {
    if (!harness) return;

    const model = harness.getModel();
    if (!model) {
      addLog('No model found. Add a model first.');
      return;
    }

    model.startStreaming();
    setIsStreaming(true);
    addLog('Started 4K streaming at maximum frame rate');
  };

  const handleStopStreaming = () => {
    if (!harness) return;

    const model = harness.getModel();
    if (model) {
      model.stopStreaming();
      setIsStreaming(false);
      addLog('Stopped streaming');
    }
  };

  const handleDisconnectAll = () => {
    if (!harness) return;

    harness.disconnectAll();
    setParticipants([]);
    setStats({});
    setIsStreaming(false);
    addLog('Disconnected all participants');
  };

  const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  const formatBandwidth = (kbps: number): string => {
    if (kbps === Infinity) return '∞';
    if (kbps >= 1000) return `${(kbps / 1000).toFixed(0)} Mbps`;
    return `${kbps} Kbps`;
  };

  return (
    <div className="min-h-screen bg-surface-dark p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Test Harness</h1>
            <p className="text-gray-400">Simulate multiple participants with bandwidth constraints</p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="btn btn-secondary"
          >
            Back to Home
          </button>
          <button
            onClick={() => navigate('/test/recording-compression')}
            className="btn btn-primary"
          >
            Recording Compression Lab
          </button>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Left: Controls */}
          <div className="space-y-6">
            {/* Room Setup */}
            <section className="bg-surface-light rounded-xl p-4 space-y-4">
              <h2 className="text-lg font-semibold">1. Create Room</h2>

              {!roomCode ? (
                <button
                  onClick={handleCreateSession}
                  className="btn btn-primary w-full"
                >
                  Create Test Room
                </button>
              ) : (
                <div className="bg-surface rounded-lg p-3">
                  <div className="text-sm text-gray-400">Room Code</div>
                  <div className="text-2xl font-mono text-accent">{roomCode}</div>
                  <button
                    onClick={() => window.open(`/s/${roomCode}/room`, '_blank')}
                    className="text-xs text-accent hover:underline mt-2"
                  >
                    Open room in new tab
                  </button>
                </div>
              )}
            </section>

            {/* Model Setup */}
            {roomCode && (
              <section className="bg-surface-light rounded-xl p-4 space-y-4">
                <h2 className="text-lg font-semibold">2. Add Model</h2>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">Resolution</label>
                  <select
                    value={modelResolution}
                    onChange={(e) => setModelResolution(e.target.value as '1080p' | '4k')}
                    className="input"
                  >
                    <option value="1080p">1080p (1920x1080)</option>
                    <option value="4k">4K (3840x2160)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">Test Pattern</label>
                  <select
                    value={modelPattern}
                    onChange={(e) => setModelPattern(e.target.value as TestPattern)}
                    className="input"
                  >
                    {PATTERNS.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">Network Profile</label>
                  <select
                    value={modelNetwork}
                    onChange={(e) => setModelNetwork(e.target.value as ProfileName)}
                    className="input"
                  >
                    {Object.entries(NETWORK_PROFILES).map(([key, profile]) => (
                      <option key={key} value={key}>
                        {profile.name} (↑{formatBandwidth(profile.uploadKbps)})
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={handleAddModel}
                  disabled={participants.some(p => p.isModel())}
                  className="btn btn-primary w-full disabled:opacity-50"
                >
                  {participants.some(p => p.isModel()) ? 'Model Added' : 'Add Model'}
                </button>
              </section>
            )}

            {/* Artists Setup */}
            {roomCode && (
              <section className="bg-surface-light rounded-xl p-4 space-y-4">
                <h2 className="text-lg font-semibold">3. Add Artists</h2>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">Number of Artists</label>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={artistCount}
                    onChange={(e) => setArtistCount(Number(e.target.value))}
                    className="input"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">Network Profile</label>
                  <select
                    value={artistNetwork}
                    onChange={(e) => setArtistNetwork(e.target.value as ProfileName)}
                    className="input"
                  >
                    {Object.entries(NETWORK_PROFILES).map(([key, profile]) => (
                      <option key={key} value={key}>
                        {profile.name} (↓{formatBandwidth(profile.downloadKbps)})
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={handleAddArtists}
                  className="btn btn-secondary w-full"
                >
                  Add {artistCount} Artists
                </button>
              </section>
            )}

            {/* Streaming Controls */}
            {participants.some(p => p.isModel()) && (
              <section className="bg-surface-light rounded-xl p-4 space-y-4">
                <h2 className="text-lg font-semibold">4. Stream</h2>

                <div className="flex gap-3">
                  {!isStreaming ? (
                    <button
                      onClick={handleStartStreaming}
                      className="btn btn-primary flex-1"
                    >
                      Start Streaming
                    </button>
                  ) : (
                    <button
                      onClick={handleStopStreaming}
                      className="btn bg-red-500 hover:bg-red-600 text-white flex-1"
                    >
                      Stop Streaming
                    </button>
                  )}
                </div>

                <button
                  onClick={handleDisconnectAll}
                  className="btn btn-secondary w-full text-red-400"
                >
                  Disconnect All
                </button>
              </section>
            )}
          </div>

          {/* Center: Preview */}
          <div className="space-y-6">
            {/* Model Preview */}
            <section className="bg-surface-light rounded-xl p-4">
              <h2 className="text-lg font-semibold mb-4">Model Preview</h2>
              <div className="aspect-video bg-surface rounded-lg overflow-hidden">
                <canvas
                  ref={previewCanvasRef}
                  width={480}
                  height={270}
                  className="w-full h-full"
                />
              </div>
              <div className="text-xs text-gray-400 mt-2 text-center">
                Live preview of test pattern (scaled down)
              </div>
            </section>

            {/* Received Frame */}
            <section className="bg-surface-light rounded-xl p-4">
              <h2 className="text-lg font-semibold mb-4">Received Frame (Artist 1)</h2>
              <div className="aspect-video bg-surface rounded-lg overflow-hidden flex items-center justify-center">
                {receivedFrameUrl ? (
                  <img
                    src={receivedFrameUrl}
                    alt="Received frame"
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <span className="text-gray-500">Waiting for frame...</span>
                )}
              </div>
              <div className="text-xs text-gray-400 mt-2 text-center">
                Shows frames received by Artist 1 after network simulation
              </div>
            </section>
          </div>

          {/* Right: Stats & Log */}
          <div className="space-y-6">
            {/* Participants */}
            <section className="bg-surface-light rounded-xl p-4">
              <h2 className="text-lg font-semibold mb-4">
                Participants ({participants.length})
              </h2>

              <div className="space-y-2 max-h-64 overflow-auto">
                {participants.map((p, i) => {
                  const pStats = stats[p.getName()];
                  return (
                    <div
                      key={i}
                      className={`bg-surface rounded-lg p-3 text-sm ${
                        p.isModel() ? 'border border-accent/50' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{p.getName()}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          p.isModel() ? 'bg-accent text-white' : 'bg-gray-600'
                        }`}>
                          {p.getRole()}
                        </span>
                      </div>
                      {pStats && (
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-400">
                          {p.isModel() ? (
                            <>
                              <div>Frames: {pStats.framesSent}</div>
                              <div>Sent: {formatBytes(pStats.bytesSent)}</div>
                            </>
                          ) : (
                            <>
                              <div>Frames: {pStats.framesReceived}</div>
                              <div>Received: {formatBytes(pStats.bytesReceived)}</div>
                            </>
                          )}
                          <div>Avg: {pStats.averageFrameTime.toFixed(0)}ms</div>
                          <div className={pStats.droppedFrames > 0 ? 'text-red-400' : ''}>
                            Dropped: {pStats.droppedFrames}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {participants.length === 0 && (
                  <div className="text-gray-500 text-sm text-center py-4">
                    No participants yet
                  </div>
                )}
              </div>
            </section>

            {/* Log */}
            <section className="bg-surface-light rounded-xl p-4">
              <h2 className="text-lg font-semibold mb-4">Event Log</h2>
              <div
                ref={logRef}
                className="bg-surface rounded-lg p-3 h-64 overflow-auto font-mono text-xs"
              >
                {log.map((entry, i) => (
                  <div key={i} className="text-gray-400 py-0.5">{entry}</div>
                ))}
                {log.length === 0 && (
                  <div className="text-gray-600">Ready to test...</div>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
